import { Options, RenderNode, RenderText } from '@contentful/rich-text-react-renderer';
import { BLOCKS, INLINES } from '@contentful/rich-text-types';
import CouponLink from 'components/commercetools-ui/atoms/coupon-link';
import { parseApplyCouponLink } from 'helpers/promoCode';
import Link from '../link';
import { RichTextProps } from './index';
import { videoPlayerRenderer } from './renderers/video-player';

const renderText: RenderText = (text) => {
  return text.split('\n').reduce((prevText, nextText, index) => {
    return [...prevText, index > 0 ? <br key={index} /> : '', nextText];
  }, new Array<string | JSX.Element>());
};

const defaultNodes: RenderNode = {
  ...videoPlayerRenderer,
  [BLOCKS.HEADING_1]: (_, children) => {
    return <h1 className="headline-1">{children}</h1>;
  },
  [BLOCKS.HEADING_2]: (_, children) => {
    return <h2 className="headline-2">{children}</h2>;
  },
  [BLOCKS.HEADING_3]: (_, children) => {
    return <h3 className="title">{children}</h3>;
  },
  [BLOCKS.HEADING_4]: (_, children) => {
    return <h4 className="description">{children}</h4>;
  },
  [BLOCKS.HEADING_5]: (_, children) => {
    return <h5 className="highlight">{children}</h5>;
  },
  [BLOCKS.HEADING_6]: (_, children) => {
    return <h6 className="subheader">{children}</h6>;
  },
  [BLOCKS.PARAGRAPH]: (_, children) => {
    return <p className="text-16 leading-loose tracking-normal">{children}</p>;
  },
  [BLOCKS.LIST_ITEM]: (_, children) => {
    return <li className="[&::marker]:text-16 [&>p]:inline">{children}</li>;
  },
  [BLOCKS.UL_LIST]: (_, children) => {
    return <ul className="list-disc pl-22">{children}</ul>;
  },
  [BLOCKS.OL_LIST]: (_, children) => {
    return <ol className="list-decimal pl-22">{children}</ol>;
  },
  [BLOCKS.QUOTE]: (_, children) => {
    return <p className="body-text-small">{children}</p>;
  },
  [INLINES.HYPERLINK]: (node, children) => {
    // Authors link to `/apply-coupon/SPRING20` to turn any phrase into a working coupon.
    // A malformed code parses to null and falls through to an ordinary link, so a typo
    // renders as a visible dead link rather than a control that silently never works.
    const couponCode = parseApplyCouponLink(node.data.uri);

    if (couponCode) {
      return <CouponLink code={couponCode}>{children}</CouponLink>;
    }

    const isInternalUrl = /^\//.test(node.data.uri);

    return (
      <Link
        className="font-bold leading-loose tracking-normal text-cta-fill-primary underline"
        target={isInternalUrl ? '_self' : '_blank'}
        link={node.data.uri}
      >
        {children}
      </Link>
    );
  },
  [BLOCKS.HR]: () => <hr className="my-16" />,
};

const smallVariantNodes: RenderNode = {
  ...defaultNodes,
  [BLOCKS.HEADING_1]: (_, children) => {
    return <h1 className="text-18 font-semibold leading-loose tracking-normal">{children}</h1>;
  },
  [BLOCKS.HEADING_2]: (_, children) => {
    return <h2 className="text-16 font-semibold leading-loose tracking-normal">{children}</h2>;
  },
  [BLOCKS.HEADING_3]: (_, children) => {
    return <h3 className="text-16 font-medium leading-loose tracking-normal">{children}</h3>;
  },
  [BLOCKS.HEADING_4]: (_, children) => {
    return <h4 className="text-14 font-semibold leading-loose tracking-normal">{children}</h4>;
  },
  [BLOCKS.HEADING_5]: (_, children) => {
    return <h5 className="text-14 font-medium leading-loose tracking-normal">{children}</h5>;
  },
  [BLOCKS.HEADING_6]: (_, children) => {
    return <h6 className="text-14 font-medium leading-loose tracking-normal">{children}</h6>;
  },
  [BLOCKS.PARAGRAPH]: (_, children) => {
    return <p className="text-14 leading-loose tracking-normal">{children}</p>;
  },
  [BLOCKS.LIST_ITEM]: (_, children) => {
    return <li className="[&::marker]:text-14 [&>p]:inline">{children}</li>;
  },
};

export const options: Record<NonNullable<RichTextProps['variant']>, Options> = {
  small: {
    renderNode: smallVariantNodes,
    renderText,
  },
  regular: {
    renderNode: defaultNodes,
    renderText,
  },
};
